import { TruncatedText } from "@core/components";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Icon } from "@core/assets";
import { formatDateRange, formatDateTime } from "@core/utils";
import { StatusBadge } from "@modules/rounds/_shared";
import { daysUntil, type PublicRoundSummary } from "../types";

/** One label/value meta row. */
function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-xs">
      <span className="shrink-0 text-sm-regular text-(--text-secondary)">
        {label}
      </span>
      <span className="min-w-0 flex-1 text-sm-medium text-(--text-primary)">
        {value}
      </span>
    </div>
  );
}

/**
 * One round in the public catalogue. Presentational — it renders a `PublicRoundSummary` and owns
 * no data fetching and no navigation.
 *
 * Not a variant of the staff `RoundCard`: that card's whole job is the eight-state action matrix
 * (edit, open now, extend, delete), and a public visitor has none of those. What is left in common
 * is the name, the badge and a date range — sharing a component for that would mean one component
 * with an audience flag threaded through every branch.
 *
 * Two authority rules the card obeys rather than re-derives:
 * - **`accepting_applications`, not `status`, decides the "open for applications" line.** A round
 *   can be `OPEN` and still refuse new applications.
 * - **`configuration_final: false` means don't promise specifics.** A `SCHEDULED` round's dates are
 *   real, but its requirements are not published yet, so the card says so instead of implying the
 *   details on screen are the final ones.
 *
 * The card's action is the entry to the whole apply funnel, and it is shown ONLY when
 * `accepting_applications` is true. A round that is visible but closed shows its status and no
 * action — an apply button that can only fail is worse on a public page than none, and the register
 * route's guard would bounce it straight back here anyway.
 */
export function PublicRoundCard({
  round,
  serverNow,
}: {
  round: PublicRoundSummary;
  /**
   * The server clock from the same response. Null before it arrives, which only suppresses the
   * countdown — the dates themselves are absolute and need no clock.
   */
  serverNow: string | null;
}) {
  const { t, i18n } = useTranslation("acquire");

  const dates =
    formatDateRange(round.opening_at, round.closing_at, i18n.language) ??
    t("rounds.no-dates");

  /**
   * The countdown, measured against the SERVER's clock.
   *
   * Which end is counted follows the round's own state: a round not yet open counts down to
   * opening, a live one to closing. Both fall silent once the clock is missing or the instant has
   * passed, rather than printing "in 0 days" — the badge already says what state the round is in.
   */
  const countdown = (() => {
    if (serverNow === null) return null;
    const isLive = round.status === "OPEN" || round.status === "CLOSING_SOON";
    const days = daysUntil(
      isLive ? round.closing_at : round.opening_at,
      serverNow
    );
    if (days === null || days === 0) return null;
    return isLive
      ? t("public.card.closes-in", { count: days })
      : t("public.card.opens-in", { count: days });
  })();

  return (
    // Same card chrome as the staff grid — 16px inline padding at 320px, 24px from `sm:` up, and
    // `h-full` + the `mt-auto` footer so cards in a row share a height instead of hugging content.
    <article className="flex h-full flex-col gap-md rounded-lg border border-(--border-default) bg-surface-card px-md py-lg shadow-sm sm:px-xl">
      <div className="flex items-start gap-xs">
        <TruncatedText
          as="h3"
          lines={2}
          className="flex-1 text-lg-semibold text-(--text-primary)"
        >
          {round.name}
        </TruncatedText>
        <StatusBadge status={round.status} />
      </div>

      {/* Clamped rather than truncated at a character count: three lines keeps the grid rows even
          without cutting a sentence mid-word in whichever of the three languages is longest. */}
      {round.description ? (
        <TruncatedText
          as="p"
          lines={3}
          className="text-sm-regular text-(--text-secondary)"
        >
          {round.description}
        </TruncatedText>
      ) : null}

      <div className="flex flex-col gap-xxs">
        <MetaRow label={t("public.card.dates")} value={dates} />
        {/* The zone the deadline is authored in. Shown because the closing instant is rendered in
            the VISITOR's zone — without it, someone abroad reading "30 Sep, 22:59" has no way to
            know which midnight the round actually closes at. */}
        <MetaRow label={t("public.card.timezone")} value={round.timezone} />
      </div>

      <div className="mt-auto flex flex-col gap-xs pt-xs">
        {countdown ? (
          <p className="inline-flex items-center gap-xs text-sm-medium text-(--text-primary)">
            <Icon name="calendar" size={16} />
            {countdown}
          </p>
        ) : null}

        <p
          className={
            round.accepting_applications
              ? "inline-flex items-center gap-xs text-sm-medium text-(--status-open-text)"
              : "inline-flex items-center gap-xs text-sm-regular text-(--text-secondary)"
          }
        >
          <Icon
            name={round.accepting_applications ? "check" : "alert"}
            size={16}
          />
          {round.accepting_applications
            ? t("public.card.accepting")
            : t("public.card.not-accepting")}
        </p>

        {round.configuration_final ? null : (
          <p className="text-xs-regular text-(--text-secondary)">
            {t("public.card.not-final", {
              datetime:
                formatDateTime(round.opening_at, i18n.language) ??
                t("rounds.no-dates"),
            })}
          </p>
        )}

        {/*
          A real `<Link>`, not a Button with an onPress: this is navigation, so it must be
          middle-clickable, copyable and crawlable — and on a public page that matters. Styled as a
          primary button rather than using the Button primitive, because that primitive renders a
          real `<button>`, and a button inside an anchor is invalid HTML.

          `min-h-11` keeps the touch target at ~44px per the a11y floor at 320px.
        */}
        {/*
          The card leads to the ROUND, not to account creation.

          It pointed at `/apply/$roundId/register` until this pass, which put the account wall
          between a visitor and the questions — the exact sequence US-E11-01 was written to undo
          ("created an account, verified an email, reached step three, and only then learned they
          might not qualify"). The Aperçu now sits in between, and the CTA lives at the end of it.

          Shown for every round rather than only accepting ones: the preview is a read, and a closed
          round still has a dossier worth reading — public DETAIL serves `CLOSED` even though the
          catalogue does not list it. The card's own status line already says whether applications
          are open, so the label stays honest about what the link does.
        */}
        <Link
          to="/rounds/$roundId"
          params={{ roundId: round.id }}
          className="mt-xs inline-flex min-h-11 w-full items-center justify-center gap-xs rounded-md bg-brand-600 px-md py-2.5 text-sm-semibold text-(--text-on-brand) outline-none transition-colors hover:bg-brand-700 focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          {t("public.card.view-round")}
          <Icon name="chevron-right" size={18} />
        </Link>
      </div>
    </article>
  );
}
